import { UseQueryOptions } from 'react-query/types/react/types';
import { useQuery } from 'react-query';
import { QueryKey } from 'src/models/query';
import { useAPIs } from 'src/hooks/useAPIs';
import { APIs } from 'src/models/apis';
import { omit } from 'rambda';
import { NeauviaItem } from 'src/models/directus';
import memoizee from 'memoizee';
import { queryCommonOptions } from 'src/utils/queryCommonOptions';
import { BaseShowcaseQuerySectionData } from 'src/modules/materials/models/showcase';
import {
  hiddenFilters,
  mapSearchFiltersToQuery,
  SearchFilters,
  SearchItemsByFiltersResult,
} from 'src/services/search';
import {
  fetchNeauviaItems,
  NeauviaItemQueryFilters,
} from 'src/modules/materials/hooks/useNeauviaItemsListQuery';

export const checkActiveFilters = memoizee(
  (filters: SearchFilters) =>
    Object.values(omit(hiddenFilters, filters)).filter((v) => !!v).length > 0,
  {
    max: 1,
  },
);

export const searchItemsByFilters = async <T extends NeauviaItem = NeauviaItem>(
  apis: APIs,
  filters: SearchFilters,
  variables?: BaseShowcaseQuerySectionData['variables'],
): Promise<SearchItemsByFiltersResult<T>> => {
  if (!checkActiveFilters(filters)) {
    return { count: -1, items: [] };
  }

  const itemsToQuery = variables?.itemsToQuery ?? ['materials', 'videos'];

  const preparedFilters = mapSearchFiltersToQuery<T>(filters);

  const items = (await fetchNeauviaItems(apis, {
    ...variables,
    itemsToQuery,
    filters: preparedFilters as NeauviaItemQueryFilters,
  })) as T[];

  return { count: items?.length ?? 0, items };
};

export const searchItemsByFiltersQuery = (
  apis: APIs,
  filters: SearchFilters,
  variables: BaseShowcaseQuerySectionData['variables'],
): UseQueryOptions<SearchItemsByFiltersResult> => {
  return {
    queryKey: [...QueryKey.SearchResults, filters, variables],
    ...queryCommonOptions.commonHandlers,
    ...queryCommonOptions.longStale,
    queryFn: () => searchItemsByFilters(apis, filters, variables),
  };
};

export const useSearchItemsByFiltersQuery = (
  filters: SearchFilters,
  variables?: BaseShowcaseQuerySectionData['variables'],
) => {
  const apis = useAPIs();

  return useQuery(searchItemsByFiltersQuery(apis, filters, variables));
};
